import { ColorType, createChart, CrosshairMode } from 'lightweight-charts'
import {
  CHART_RESOLUTIONS,
  fetchCandles,
  resolveMarketId,
} from '../../lib/lighterApi'

const UP = '#3cffb2'
const DOWN = '#ff3b69'
const GRID = 'rgba(255,255,255,0.04)'
/* lightweight-charts cannot parse oklch(... / alpha); use rgba. */
const TEXT = 'rgba(245, 245, 245, 0.55)'

/**
 * Exact TradingView left toolbar as used on Lighter:
 * 14 icons · 4 sections
 *
 * 1) Cursor
 * 2) Drawing groups (trend, fib/gann, brush, text, patterns)
 * 3) Forecast / icons / measure / zoom
 * 4) Magnet / keep drawing / lock / hide  (pinned)
 */
const TOOLBAR_SECTIONS = [
  {
    id: 'cursor',
    tools: [
      {
        id: 'cursor',
        label: 'Crosshair',
        group: true,
        svg: `
          <path d="M8 1.25v13.5M1.25 8h13.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/>
          <circle cx="8" cy="8" r="1.15" fill="currentColor"/>`,
      },
    ],
  },
  {
    id: 'drawings',
    tools: [
      {
        id: 'trend',
        label: 'Trend Line Tools',
        group: true,
        svg: `
          <path d="M2.4 12.7 13.6 3.3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
          <circle cx="2.4" cy="12.7" r="1.25" fill="currentColor"/>
          <circle cx="13.6" cy="3.3" r="1.25" fill="currentColor"/>`,
      },
      {
        id: 'fib',
        label: 'Gann and Fibonacci Tools',
        group: true,
        svg: `
          <path d="M2.5 13.2 8 2.8 13.5 13.2" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <path d="M4.4 9.6h7.2" stroke="currentColor" stroke-width="1.15" stroke-linecap="round"/>`,
      },
      {
        id: 'brush',
        label: 'Geometric Shapes',
        group: true,
        svg: `
          <path d="M3.2 11.8c1.7-3.1 5-6.5 8.8-7.6l.9.9c-1.1 3.8-4.5 7.1-7.6 8.8l-2.1-2.1Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" fill="none"/>
          <path d="M4.1 12.6 3 14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
      },
      {
        id: 'text',
        label: 'Annotation Tools',
        group: true,
        svg: `
          <path d="M3.2 3.4h9.6M8 3.4v9.4" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/>
          <path d="M5.2 12.8h5.6" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/>`,
      },
      {
        id: 'patterns',
        label: 'Patterns',
        group: true,
        svg: `
          <path d="M2.4 11.8 5.2 4.2 8.2 9.6 10.8 3.6 13.6 11.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <circle cx="2.4" cy="11.8" r="1.05" fill="currentColor"/>
          <circle cx="5.2" cy="4.2" r="1.05" fill="currentColor"/>
          <circle cx="8.2" cy="9.6" r="1.05" fill="currentColor"/>
          <circle cx="10.8" cy="3.6" r="1.05" fill="currentColor"/>
          <circle cx="13.6" cy="11.2" r="1.05" fill="currentColor"/>`,
      },
    ],
  },
  {
    id: 'measure',
    tools: [
      {
        id: 'long-position',
        label: 'Prediction and Measurement Tools',
        group: true,
        svg: `
          <rect x="3" y="2.8" width="10" height="10.4" rx="1.1" stroke="currentColor" stroke-width="1.2" fill="none"/>
          <path d="M3.1 8h9.8" stroke="currentColor" stroke-width="1.1"/>
          <path d="M3.2 3.1h9.6v4.8H3.2Z" fill="currentColor" opacity=".28"/>
          <path d="M3.2 8.2h9.6v4.8H3.2Z" fill="currentColor" opacity=".12"/>`,
      },
      {
        id: 'icons',
        label: 'Icons',
        group: true,
        svg: `
          <circle cx="8" cy="8" r="5.4" stroke="currentColor" stroke-width="1.25" fill="none"/>
          <circle cx="5.8" cy="6.7" r="0.85" fill="currentColor"/>
          <circle cx="10.2" cy="6.7" r="0.85" fill="currentColor"/>
          <path d="M5.5 9.6c.8 1.3 1.9 1.9 2.5 1.9s1.7-.6 2.5-1.9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" fill="none"/>`,
      },
      {
        id: 'measure',
        label: 'Measure',
        svg: `
          <path d="M2.8 13.2 13.2 2.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
          <path d="M2.8 13.2h2.8M10.4 2.8h2.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
          <path d="M5.2 10.8l1.1-1.1M7.1 8.9l1.1-1.1M9 7l1.1-1.1" stroke="currentColor" stroke-width="1.15" stroke-linecap="round"/>`,
      },
      {
        id: 'zoom',
        label: 'Zoom In',
        svg: `
          <circle cx="7" cy="7" r="4.35" stroke="currentColor" stroke-width="1.3" fill="none"/>
          <path d="M10.25 10.25 14 14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
          <path d="M5.2 7h3.6M7 5.2v3.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
      },
    ],
  },
  {
    id: 'utilities',
    tools: [
      {
        id: 'magnet',
        label: 'Magnet Mode',
        toggle: true,
        svg: `
          <path d="M4.6 2.6v5.4a3.4 3.4 0 006.8 0V2.6" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" fill="none"/>
          <path d="M4.6 2.6H3M11.4 2.6H13M3 2.6v2.4M13 2.6v2.4" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/>`,
      },
      {
        id: 'keep-drawing',
        label: 'Stay in Drawing Mode',
        toggle: true,
        svg: `
          <path d="M2.8 10.9 9.7 4a1.2 1.2 0 011.7 1.7L4.5 12.6 2.4 13.2 2.8 10.9Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" fill="none"/>
          <path d="M8.4 5.3 10.1 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
          <rect x="9.2" y="9.4" width="4.4" height="3.6" rx="0.7" stroke="currentColor" stroke-width="1.1" fill="none"/>
          <path d="M10.2 9.4V8.5a1.2 1.2 0 012.4 0v.9" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" fill="none"/>`,
      },
      {
        id: 'lock',
        label: 'Lock All Drawing Tools',
        toggle: true,
        svg: `
          <rect x="3.4" y="7.1" width="9.2" height="6.2" rx="1.3" stroke="currentColor" stroke-width="1.25" fill="none"/>
          <path d="M5.4 7.1V5.4a2.6 2.6 0 015.2 0v1.7" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" fill="none"/>`,
      },
      {
        id: 'hide',
        label: 'Hide All Drawing Tools',
        toggle: true,
        svg: `
          <path d="M1.8 8s2.4-4.4 6.2-4.4S14.2 8 14.2 8s-2.4 4.4-6.2 4.4S1.8 8 1.8 8Z" stroke="currentColor" stroke-width="1.25" fill="none"/>
          <circle cx="8" cy="8" r="1.7" stroke="currentColor" stroke-width="1.25" fill="none"/>
          <path d="M3.2 12.8 12.8 3.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
      },
    ],
  },
]

function toolButton(tool, { active = false } = {}) {
  const groupClass = tool.group ? ' trade-chart-toolbar__btn--group' : ''
  const activeClass = active ? ' is-active' : ''
  return `<button type="button" class="trade-chart-toolbar__btn${groupClass}${activeClass}" data-tool="${tool.id}" data-toggle="${tool.toggle ? 'true' : 'false'}" title="${tool.label}" aria-label="${tool.label}" aria-pressed="${active ? 'true' : 'false'}">
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">${tool.svg}</svg>
    ${tool.group ? '<span class="trade-chart-toolbar__caret" aria-hidden="true"></span>' : ''}
  </button>`
}

function buildChartToolbar() {
  const bar = document.createElement('div')
  bar.className = 'trade-chart-toolbar'
  bar.setAttribute('data-testid', 'chart-drawing-toolbar')
  bar.setAttribute('role', 'toolbar')
  bar.setAttribute('aria-label', 'Chart tools')

  const scroll = document.createElement('div')
  scroll.className = 'trade-chart-toolbar__scroll'
  scroll.setAttribute('data-testid', 'chart-toolbar-scroll')

  TOOLBAR_SECTIONS.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      const divider = document.createElement('div')
      divider.className = 'trade-chart-toolbar__divider'
      divider.setAttribute('aria-hidden', 'true')
      scroll.appendChild(divider)
    }

    const wrap = document.createElement('div')
    wrap.className = 'trade-chart-toolbar__section'
    wrap.setAttribute('data-section', section.id)
    wrap.innerHTML = section.tools
      .map((tool, i) =>
        toolButton(tool, {
          active: section.id === 'cursor' && i === 0,
        })
      )
      .join('')
    scroll.appendChild(wrap)
  })

  bar.appendChild(scroll)
  return bar
}

/**
 * Mount a live Lighter candlestick chart into `#tv-price-chart`.
 * @param {HTMLElement} page
 * @param {{ symbol?: string }} [opts]
 * @returns {() => void} cleanup
 */
export function mountLivePriceChart(page, { symbol = 'LIT' } = {}) {
  const host = page.querySelector('#tv-price-chart')
  if (!host) return () => {}

  host
    .querySelectorAll('.trade-chart-shot, .trade-live-chart, .trade-chart-toolbar')
    .forEach((el) => el.remove())
  const iframe = host.querySelector('iframe')
  if (iframe) {
    iframe.style.display = 'none'
    iframe.setAttribute('src', 'about:blank')
  }

  host.classList.add('trade-chart-host')

  const toolbar = buildChartToolbar()
  host.appendChild(toolbar)

  const root = document.createElement('div')
  root.className = 'trade-live-chart'
  root.setAttribute('data-testid', 'live-price-chart')
  host.appendChild(root)

  const onToolbarClick = (event) => {
    const btn = event.target.closest('[data-tool]')
    if (!btn || !toolbar.contains(btn)) return
    const isToggle = btn.getAttribute('data-toggle') === 'true'
    if (isToggle) {
      const on = btn.getAttribute('aria-pressed') !== 'true'
      btn.classList.toggle('is-active', on)
      btn.setAttribute('aria-pressed', on ? 'true' : 'false')
      return
    }
    toolbar.querySelectorAll('[data-tool]').forEach((el) => {
      if (el.getAttribute('data-toggle') === 'true') return
      const on = el === btn
      el.classList.toggle('is-active', on)
      el.setAttribute('aria-pressed', on ? 'true' : 'false')
    })
  }
  toolbar.addEventListener('click', onToolbarClick)

  const themeColors = () => {
    const light =
      document.documentElement.getAttribute('data-theme') === 'light'
    return {
      plotBg: light ? '#ffffff' : '#131317',
      plotText: light ? 'rgba(25, 27, 29, 0.55)' : TEXT,
      plotGrid: light ? 'rgba(0, 0, 0, 0.06)' : GRID,
      cross: light ? 'rgba(25, 27, 29, 0.25)' : 'rgba(243,243,243,0.25)',
      crossLabel: light ? '#e8e8ea' : '#2a2a30',
    }
  }

  const colors = themeColors()
  const chart = createChart(root, {
    autoSize: true,
    layout: {
      background: { type: ColorType.Solid, color: colors.plotBg },
      textColor: colors.plotText,
      fontFamily:
        'Inter, system-ui, Segoe UI, Roboto, Helvetica Neue, sans-serif',
    },
    grid: {
      vertLines: { color: colors.plotGrid },
      horzLines: { color: colors.plotGrid },
    },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: {
        color: colors.cross,
        labelBackgroundColor: colors.crossLabel,
      },
      horzLine: {
        color: colors.cross,
        labelBackgroundColor: colors.crossLabel,
      },
    },
    rightPriceScale: {
      borderVisible: false,
      scaleMargins: { top: 0.08, bottom: 0.18 },
    },
    timeScale: {
      borderVisible: false,
      timeVisible: true,
      secondsVisible: false,
    },
    handleScroll: { vertTouchDrag: false },
  })

  const candles = chart.addCandlestickSeries({
    upColor: UP,
    downColor: DOWN,
    borderUpColor: UP,
    borderDownColor: DOWN,
    wickUpColor: UP,
    wickDownColor: DOWN,
  })

  const volume = chart.addHistogramSeries({
    priceFormat: { type: 'volume' },
    priceScaleId: '',
  })
  chart.priceScale('').applyOptions({
    scaleMargins: { top: 0.82, bottom: 0 },
  })
  chart.priceScale('right').applyOptions({
    scaleMargins: { top: 0.08, bottom: 0.18 },
  })

  let resolution = '5m'
  let marketId = null
  let pollTimer = null
  let disposed = false
  let loading = false

  const setStatus = (text) => {
    let el = root.querySelector('.trade-live-chart__status')
    if (!text) {
      el?.remove()
      return
    }
    if (!el) {
      el = document.createElement('div')
      el.className = 'trade-live-chart__status'
      root.appendChild(el)
    }
    el.textContent = text
  }

  const applyBars = (bars) => {
    candles.setData(
      bars.map(({ time, open, high, low, close }) => ({
        time,
        open,
        high,
        low,
        close,
      }))
    )
    volume.setData(
      bars.map(({ time, close, open, value }) => ({
        time,
        value,
        color: close >= open ? 'rgba(60,255,178,0.35)' : 'rgba(255,59,105,0.35)',
      }))
    )
    chart.timeScale().scrollToRealTime()
  }

  const load = async ({ silent = false } = {}) => {
    if (disposed || loading) return
    loading = true
    if (!silent) setStatus('Loading…')
    try {
      if (marketId == null) {
        marketId = await resolveMarketId(symbol)
        if (marketId == null) throw new Error(`Unknown market ${symbol}`)
      }
      const bars = await fetchCandles({
        marketId,
        resolution,
        countBack: 400,
      })
      if (disposed) return
      if (!bars.length) {
        setStatus('No candle data')
        return
      }
      applyBars(bars)
      setStatus('')
    } catch (err) {
      console.error('[live-chart]', err)
      if (!silent) setStatus('Chart unavailable')
    } finally {
      loading = false
    }
  }

  const setResolution = (next) => {
    if (
      !CHART_RESOLUTIONS[next] &&
      !Object.values(CHART_RESOLUTIONS).includes(next)
    ) {
      return
    }
    const resolved = CHART_RESOLUTIONS[next] || next
    if (resolved === resolution) return
    resolution = resolved
    syncTimeframeButtons(page, resolution)
    load()
  }

  const onPageClick = (event) => {
    const btn = event.target.closest(
      '#tv-chart-container button, [data-chart-resolution]'
    )
    if (!btn || !page.contains(btn)) return
    if (btn.closest('.trade-chart-toolbar')) return
    const label = (btn.textContent || '').replace(/\s+/g, ' ').trim()
    const mapped = labelToResolution(label)
    if (mapped) {
      event.preventDefault()
      event.stopPropagation()
      setResolution(mapped)
    }
  }

  page.addEventListener('click', onPageClick, true)
  syncTimeframeButtons(page, resolution)
  load()
  pollTimer = window.setInterval(() => load({ silent: true }), 15_000)

  const applyTheme = () => {
    const next = themeColors()
    chart.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: next.plotBg },
        textColor: next.plotText,
      },
      grid: {
        vertLines: { color: next.plotGrid },
        horzLines: { color: next.plotGrid },
      },
      crosshair: {
        vertLine: {
          color: next.cross,
          labelBackgroundColor: next.crossLabel,
        },
        horzLine: {
          color: next.cross,
          labelBackgroundColor: next.crossLabel,
        },
      },
    })
  }
  const themeObserver = new MutationObserver(applyTheme)
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  })

  return () => {
    disposed = true
    themeObserver.disconnect()
    toolbar.removeEventListener('click', onToolbarClick)
    page.removeEventListener('click', onPageClick, true)
    if (pollTimer) window.clearInterval(pollTimer)
    chart.remove()
    root.remove()
    toolbar.remove()
  }
}

function labelToResolution(label) {
  const map = {
    '1m': '1m',
    '5m': '5m',
    '15m': '15m',
    '1h': '1h',
    '4h': '4h',
    '1d': '1d',
    D: '1d',
  }
  return map[label] || null
}

function syncTimeframeButtons(page, resolution) {
  const container = page.querySelector('#tv-chart-container')
  if (!container) return
  const labels = {
    '1m': '1m',
    '5m': '5m',
    '15m': '15m',
    '1h': '1h',
    '4h': '4h',
    '1d': '1d',
  }
  const activeLabel = labels[resolution] || '5m'
  container.querySelectorAll('button').forEach((btn) => {
    if (btn.closest('.trade-chart-toolbar')) return
    const label = (btn.textContent || '').replace(/\s+/g, ' ').trim()
    if (!labelToResolution(label)) return
    const on = label === activeLabel || (activeLabel === '1d' && label === 'D')
    btn.setAttribute('aria-pressed', on ? 'true' : 'false')
    btn.classList.toggle('text-ink', on)
    btn.classList.toggle('text-muted', !on)
  })
}
